import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAdminAuth } from '@/hooks/useAdminAuth';

// Credentials stored in DB: username=admin, password stored as plain text in value column
// For production, use an edge function with bcrypt. This is a simple admin-only panel.
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check credentials against site_settings where we store admin password
      const { data: pwRow } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'admin_password')
        .maybeSingle();

      const { data: userRow } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'admin_username')
        .maybeSingle();

      const storedUser = userRow?.value || 'admin';
      const storedPass = pwRow?.value || 'admin123';

      if (username.trim() === storedUser && password === storedPass) {
        login(username.trim());
        navigate('/admin');
      } else {
        setError('Usuário ou senha incorretos.');
      }
    } catch {
      setError('Erro ao conectar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-[#C9A84C]/30 mb-4">
            <i className="ri-shield-keyhole-line text-[#C9A84C] text-2xl"></i>
          </div>
          <h1 className="text-2xl font-semibold text-white tracking-widest uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Ma Gádeni
          </h1>
          <p className="text-[#C9A84C]/60 text-sm mt-1 tracking-wider">Painel Administrativo</p>
        </div>

        {/* Card */}
        <div className="bg-[#111111] border border-white/5 rounded-xl p-8">
          <h2 className="text-white text-lg font-medium mb-6">Entrar na área restrita</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs text-white/50 uppercase tracking-wider mb-2">Usuário</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-white/30 text-sm"></i>
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-white/50 uppercase tracking-wider mb-2">Senha</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-white/30 text-sm"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-10 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-sm`}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line text-red-400 text-sm"></i>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C9A84C] hover:bg-[#D4AF37] text-black font-semibold py-3 rounded-lg text-sm tracking-wider uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Entrando...
                </span>
              ) : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5">
            <p className="text-white/20 text-xs text-center">
              Acesso restrito a administradores autorizados
            </p>
          </div>
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          <a href="/" className="hover:text-[#C9A84C] transition-colors">← Voltar ao site</a>
        </p>
      </div>
    </div>
  );
}
